import { ALWAYS_INBOX_MS_PLATFORMS, INBOX_MS_API_ALLOWED_PLATFORMS } from './inboxMsChatPlatform';

export const SIDEBAR_PLATFORM_KEYS = [
  'whatsapp',
  'messenger',
  'instagram',
  'facebook_comments',
  'instagram_comments',
  'tik_tok_messages',
  'tik_tok_comments'
];

const MS_COUNTER_RESPONSE_FIELDS = {
  messenger: 'unread_messenger_chats_count',
  whatsapp: 'unread_whatsapp_chats_count',
  instagram: 'unread_instagram_chats_count',
  tik_tok_messages: 'unread_tiktok_chats_count',
  tik_tok_comments: 'unread_tiktok_comments_count'
};

/** MS platform API name → sidebar key (contrato de eventos chat_counter_update) */
const MS_PLATFORM_TO_SIDEBAR_KEY = {
  facebook_messages: 'messenger',
  whatsapp_bsp_messages: 'whatsapp',
  whatsapp_qr_messages: 'whatsapp',
  whatsapp_gupshup_messages: 'whatsapp',
  instagram_messages: 'instagram',
  tik_tok_messages: 'tik_tok_messages',
  tik_tok_comments: 'tik_tok_comments'
};

/** Flag field in chat_counter_update changes → sidebar key */
const MS_COUNTER_FLAG_FIELDS = {
  unread_messenger_chat: 'messenger',
  unread_whatsapp_chat: 'whatsapp',
  unread_instagram_chat: 'instagram',
  unread_tiktok_chat: 'tik_tok_messages',
  unread_tiktok_comment: 'tik_tok_comments'
};

/** Count field in chat_counter_update changes → canonical conversation field */
const MS_COUNTER_COUNT_FIELDS = {
  unread_messages_count: 'unread_messages_count',
  unread_whatsapp_messages: 'unread_messages_count',
  unread_messenger_messages: 'unread_messages_count',
  unread_instagram_messages: 'unread_messages_count',
  unread_tiktok_messages: 'unread_messages_count',
  unread_tiktok_comments: 'unread_messages_count'
};

/** Flag fields in chat_counter_update changes → canonical conversation flag */
const MS_CONVERSATION_FLAG_FIELDS = new Set([
  ...Object.keys(MS_COUNTER_FLAG_FIELDS),
  'has_unread_messages',
  'unread'
]);

function safeCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function arrayLength(data, platformKey) {
  const arr = data?.[platformKey];
  return Array.isArray(arr) ? arr.length : 0;
}

/**
 * Valida si el valor es una plataforma conocida del inbox (sidebar o MS).
 * @param {string|undefined|null} platform
 * @returns {string|undefined}
 */
export function resolveInboxPlatform(platform) {
  if (!platform) return undefined;
  if (SIDEBAR_PLATFORM_KEYS.includes(platform)) return platform;
  if (INBOX_MS_API_ALLOWED_PLATFORMS.has(platform)) return platform;
  return undefined;
}

/**
 * Resuelve la clave de sidebar para contadores/almacenamiento local.
 * @param {string|undefined|null} platform
 * @returns {string|undefined}
 */
export function resolveSidebarCounterPlatform(platform) {
  const resolved = resolveInboxPlatform(platform);
  if (!resolved) return undefined;
  if (SIDEBAR_PLATFORM_KEYS.includes(resolved)) return resolved;
  return resolveSidebarKeyFromMsPlatform(resolved);
}

const ROLLOUT_FLAG_BY_SIDEBAR_KEY = {
  messenger: 'facebook_messages',
  whatsapp: 'whatsapp_messages',
  instagram: 'instagram_messages'
};

/**
 * ¿Este canal del sidebar sigue contándose con los arrays del monolito?
 * Misma regla que `mergePlatformCounts`: solo un `true` explícito en
 * `retailer_info.inbox_microservice_platforms` cede el canal al MS. El estado de
 * módulo de `inboxMsChatPlatform` no participa, porque puede quedar en `true`
 * después de un retailer_info viejo.
 * TikTok no tiene ese flag: siempre lo sirve el MS.
 *
 * @param {string} platformKey - Sidebar platform key
 * @param {Object|null|undefined} [msPlatforms]
 * @returns {boolean}
 */
export function shouldUseMonolithCounterSource(platformKey, msPlatforms) {
  if (!platformKey) return true;
  if (ALWAYS_INBOX_MS_PLATFORMS.has(platformKey)) return false;
  const flagKey = ROLLOUT_FLAG_BY_SIDEBAR_KEY[platformKey];
  if (!flagKey) return true;
  return msPlatforms?.[flagKey] !== true;
}

/**
 * El monolito ya entregó arrays por canal. `null` y `{}` no son una base de merge:
 * publicarían el sidebar en cero.
 *
 * @param {Object|null|undefined} data
 * @returns {boolean}
 */
export function hasMonolithCounterPayload(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  return SIDEBAR_PLATFORM_KEYS.some((key) => Array.isArray(data[key]));
}

/**
 * @param {Object|null|undefined} unreadChatsData
 * @returns {Object.<string, number>}
 */
export function deriveMonolithCounts(unreadChatsData) {
  return SIDEBAR_PLATFORM_KEYS.reduce((acc, key) => {
    acc[key] = arrayLength(unreadChatsData, key);
    return acc;
  }, {});
}

function readMsCounter(msCounters, platformKey) {
  const field = MS_COUNTER_RESPONSE_FIELDS[platformKey];
  if (!field || !msCounters) return 0;
  return safeCount(msCounters[field]);
}

/**
 * Combina contadores monolito (arrays) + MS según flags de migración.
 *
 * El hash `msPlatforms` (`retailer_info.inbox_microservice_platforms`) es la
 * única fuente para decidir si el MS reemplaza a los arrays del monolito. No
 * se consulta el estado de módulo de `inboxMsChatPlatform`: ese estado puede
 * venir de `retailer_info` persistido/rehidratado y dejar el flag en `true`
 * aunque el retailer ya no tenga el rollout, pisando el contador del monolito
 * con el agregado (posiblemente en 0) del MS.
 *
 * Si el hash todavía no está disponible, se conservan los contadores del
 * monolito; TikTok es la excepción porque siempre lo sirve el MS.
 *
 * @param {Object|null|undefined} monolithData
 * @param {Object|null|undefined} msCounters
 * @param {Object|null|undefined} [msPlatforms] - Hash de plataformas MS habilitadas desde Redux.
 *        Solo un valor `true` habilita el reemplazo; si falta, no se reemplaza.
 * @param {Object|null|undefined} [msMigratingPlatforms] - Hash de plataformas cuya migración
 *        sigue en curso (`retailer_info.inbox_microservice_migrating_platforms`). Para ellas el
 *        contador del MS se SUMA al del monolito en vez de reemplazarlo.
 * @returns {Object.<string, number>}
 */
export function mergePlatformCounts(monolithData, msCounters, msPlatforms = null, msMigratingPlatforms = null) {
  const merged = deriveMonolithCounts(monolithData);

  const isMsPlatformEnabled = (key) => msPlatforms?.[key] === true;

  // Durante la migración cada chat no leído está en una fuente o en la otra —la
  // frontera es `ws_active`, y el array del monolito ya la respeta—, así que
  // reemplazar perdería la mitad que todavía no se ha copiado.
  const isMigrating = (key) => msMigratingPlatforms?.[key] === true;

  merged.tik_tok_messages = readMsCounter(msCounters, 'tik_tok_messages');
  merged.tik_tok_comments = readMsCounter(msCounters, 'tik_tok_comments');
  if (isMsPlatformEnabled('facebook_messages')) {
    merged.messenger = readMsCounter(msCounters, 'messenger');
  }
  if (isMsPlatformEnabled('whatsapp_messages')) {
    merged.whatsapp = isMigrating('whatsapp_messages')
      ? safeCount(merged.whatsapp) + readMsCounter(msCounters, 'whatsapp')
      : readMsCounter(msCounters, 'whatsapp');
  }
  if (isMsPlatformEnabled('instagram_messages')) {
    merged.instagram = readMsCounter(msCounters, 'instagram');
  }

  return merged;
}

/**
 * @param {Object.<string, number>} platformCounts
 * @param {string} platformKey
 * @param {number} delta
 * @returns {Object.<string, number>}
 */
export function applyCounterDelta(platformCounts, platformKey, delta) {
  const current = safeCount(platformCounts?.[platformKey]);
  const next = Math.max(0, current + delta);
  return { ...platformCounts, [platformKey]: next };
}

/**
 * Resuelve sidebar key desde event.platform del MS (chat_counter_update).
 * @param {string|undefined|null} msPlatform
 * @returns {string|undefined}
 */
export function resolveSidebarKeyFromMsPlatform(msPlatform) {
  if (!msPlatform) return undefined;
  return MS_PLATFORM_TO_SIDEBAR_KEY[msPlatform];
}

/**
 * Calcula delta de chats no leídos desde tuplas [prev, next] del flag field.
 * @param {Object|null|undefined} changes
 * @returns {{ platformKey: string, delta: number }|null}
 */
export function parseMsCounterEventDelta(changes) {
  if (!changes || typeof changes !== 'object') return null;

  const match = Object.entries(changes).find(([field, tuple]) => {
    const platformKey = MS_COUNTER_FLAG_FIELDS[field];
    return platformKey
      && Array.isArray(tuple)
      && tuple.length >= 2
      && Boolean(tuple[0]) !== Boolean(tuple[1]);
  });

  if (!match) return null;

  const [field, tuple] = match;
  return {
    platformKey: MS_COUNTER_FLAG_FIELDS[field],
    delta: tuple[1] ? 1 : -1
  };
}

/**
 * Construye los cambios de conversación (flag + conteo) desde un evento chat_counter_update.
 * @param {Object} event
 * @returns {{ has_unread_messages?: boolean, unread_messages_count?: number }|null}
 */
export function buildConversationChangesFromCounterEvent(event) {
  const changes = event?.changes;
  if (!changes || typeof changes !== 'object') return null;

  const conversationChanges = {};

  Object.entries(changes).forEach(([field, tuple]) => {
    if (!Array.isArray(tuple) || tuple.length < 2) return;

    const nextValue = tuple[1];

    if (Object.prototype.hasOwnProperty.call(MS_COUNTER_COUNT_FIELDS, field)) {
      conversationChanges.unread_messages_count = safeCount(nextValue);
    }

    if (MS_CONVERSATION_FLAG_FIELDS.has(field)) {
      conversationChanges.has_unread_messages = Boolean(nextValue);
    }
  });

  return Object.keys(conversationChanges).length > 0 ? conversationChanges : null;
}

/**
 * Aplica evento chat_counter_update del MS sobre platformCounts.
 * @param {Object.<string, number>} platformCounts
 * @param {Object} event
 * @param {Object|null|undefined} [msPlatforms] - `retailer_info.inbox_microservice_platforms`
 * @returns {{ counts: Object.<string, number>, applied: boolean }}
 */
export function applyMsCounterEvent(platformCounts, event, msPlatforms) {
  const sidebarKey = resolveSidebarKeyFromMsPlatform(event?.platform);
  if (!sidebarKey || shouldUseMonolithCounterSource(sidebarKey, msPlatforms)) {
    return { counts: platformCounts, applied: false };
  }

  const parsed = parseMsCounterEventDelta(event?.changes);
  if (!parsed || parsed.platformKey !== sidebarKey) {
    return { counts: platformCounts, applied: false };
  }

  return {
    counts: applyCounterDelta(platformCounts, sidebarKey, parsed.delta),
    applied: true
  };
}

/**
 * Suma total de contadores combinados (has_unread_chats).
 * @param {Object.<string, number>|null|undefined} platformCounts
 * @returns {boolean}
 */
export function hasUnreadChatsFromCounts(platformCounts) {
  if (!platformCounts) return false;
  return SIDEBAR_PLATFORM_KEYS.some((key) => safeCount(platformCounts[key]) > 0);
}
